import {
  Button,
  classes,
  Modal,
  Module,
  Tooltip,
  useGetSiteApi,
  useSelectedSites,
  withGlobalParams,
} from "@abb-connect/react-shared";
import { useState } from "react";
import styles from "./ServiceCard.module.css";

type ServiceCardProps = {
  module: Module;
  site?: string;
  productionArea?: string;
};

function ServiceCard({ module, site, productionArea }: ServiceCardProps) {
  const { getHref, enabled, supportsMultiSite } = module;

  const { selectedSite, selectedSites, isMultiSiteMode } = useSelectedSites();
  const resolvedSite = site ?? selectedSite;
  const { data } = useGetSiteApi(resolvedSite, true, productionArea);

  const unavailableForMultiSite = isMultiSiteMode && !supportsMultiSite;

  if (unavailableForMultiSite) {
    return (
      <Tooltip
        content="This feature is not available for multiple sites."
        placement="bottom"
        anchorToCursor
        showDelay={300}
      >
        <ServiceCardContent module={module} disabled />
      </Tooltip>
    );
  }

  const href =
    enabled && data && resolvedSite
      ? getHref(
          resolvedSite,
          data.rootEquipments?.length > 0 ? data.rootEquipments[0] : "",
          data.systemOverviewReport,
          data.projectOverviewReport,
          data.productionArea
        )
      : undefined;

  return href ? (
    <a href={supportsMultiSite ? withGlobalParams(href, selectedSites) : href}>
      <ServiceCardContent module={module} />
    </a>
  ) : (
    <DisabledServiceCard module={module} />
  );
}

function DisabledServiceCard({ module }: ServiceCardProps) {
  const [modalVisible, setModalVisible] = useState(false);

  const onClicked = () => {
    setModalVisible(true);
  };

  const onDismiss = () => {
    setModalVisible(false);
  };

  return (
    <div
      onClick={onClicked}
      title="Contact your ABB representative to access this service"
    >
      <ServiceCardContent module={module} />
      <Modal visible={modalVisible} onDismiss={onDismiss}>
        <div className={styles.modal}>
          <p>
            This feature is currently not included in your plan. To access it
            please contact your ABB representative.
          </p>
          <div className={styles["modal-footer"]}>
            <Button text="Close" variant="primary" onClick={onDismiss} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

function ServiceCardContent({
  module,
  disabled = false,
}: ServiceCardProps & { disabled?: boolean }) {
  const { icon, title, description, enabled } = module;

  const isDisabled = disabled || !enabled;

  return (
    <div
      className={classes(
        styles.container,
        isDisabled ? styles.disabled : classes(styles.enabled, "fade-in")
      )}
    >
      <div className={styles.tile}>
        <div className={styles.icon}>{icon}</div>
      </div>
      <div className={styles.description}>
        <h4>{title}</h4>
        <ul>
          {description.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default ServiceCard;
